import { useVolunteeringDeleteVolunteeringRecord, type VolunteeringRecordDto } from "@crowdcoursing/api/openapi";
import { Close } from "@mui/icons-material";
import { Box, Button, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";

import { useToast } from "~hooks/useToast";
import { useVolunteeringConfirmation } from "~hooks/useVolunteeringConfirmation";
import { Route } from "~services/router/Route";
import { Link } from "~ui/atoms/Link/Link";
import { formatEventTime } from "~utils/date";

import { ConfirmDeleteDialog } from "../ConfirmDeleteDialog/ConfirmDeleteDialog";

const tVolunteeringRecord = "Volunteering Record";
const tEvent = "Event";
const tRoom = "Room";
const tOrganization = "Organization";
const tVolunteer = "Volunteer";
const tLeader = "Leader";
const tTime = "Time";
const tStatus = "Status";
const tConfirmed = "Confirmed";
const tPending = "Pending";
const tConfirm = "Confirm Volunteering Time";
const tDelete = "Delete Record";
const tConfirmedBy = "Confirmed By";
const tConfirmedOn = "Confirmed On";
const tVolunteeringTimeConfirmed = "Volunteering time confirmed successfully";
const tVolunteeringRecordDeleted = "Volunteering record deleted successfully";

interface VolunteeringRecordPanelProps {
  currentUserId: string;
  currentUserRoles?: string[];
  isOpen: boolean;
  onClose: () => void;
  record?: VolunteeringRecordDto;
  confirmed?: boolean;
}

export const VolunteeringRecordPanel = ({
  currentUserId,
  currentUserRoles = [],
  isOpen,
  onClose,
  record,
  confirmed,
}: VolunteeringRecordPanelProps) => {
  const toast = useToast();
  const { confirmVolunteering, isConfirming } = useVolunteeringConfirmation({
    onSuccess: () => {
      onClose();
      toast.success(tVolunteeringTimeConfirmed);
      setTimeout(() => window.location.reload(), 5);
    },
  });

  const { mutate: deleteRecord } = useVolunteeringDeleteVolunteeringRecord({
    onSuccess: () => {
      onClose();
      toast.success(tVolunteeringRecordDeleted);
      setTimeout(() => window.location.reload(), 5);
    },
  });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (!record) {
    return null;
  }

  const showConfirmButton =
    record.volunteerUserId !== currentUserId &&
    (currentUserRoles.includes("Admin") || currentUserRoles.includes("Educator")) &&
    !confirmed;

  const handleConfirm = () => {
    if (record.id) {
      confirmVolunteering(record.id);
    }
  };

  const handleDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (record.id) {
      deleteRecord({ pathParams: { volunteeringRecordId: record.id } });
    }
  };

  const startTime = record.startTime ? DateTime.fromISO(record.startTime).toJSDate() : null;

  return (
    <>
      <Drawer anchor="right" onClose={onClose} open={isOpen}>
        <Box sx={{ p: 3, width: 400 }}>
          <Stack direction="row" justifyContent="space-between" mb={3}>
            <Typography variant="h5">{tVolunteeringRecord}</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>

          <Stack spacing={2}>
            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                {tStatus}
              </Typography>
              <Typography>{confirmed ? tConfirmed : tPending}</Typography>
            </Box>

            {confirmed && record.whoConfirmedName && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  {tConfirmedBy}
                </Typography>
                <Typography>{record.whoConfirmedName}</Typography>
              </Box>
            )}

            {confirmed && record.whenConfirmed && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  {tConfirmedOn}
                </Typography>
                <Typography>{formatEventTime(DateTime.fromISO(record.whenConfirmed).toJSDate())}</Typography>
              </Box>
            )}

            {/* Only show Event section if we have an event ID or name */}
            {(record.eventId || record.eventName) && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  {tEvent}
                </Typography>
                {record.eventId ? (
                  <Link to={Route.eventView({ eventId: record.eventId })} type="internal">
                    {record.eventName}
                  </Link>
                ) : (
                  <Typography>{record.eventName}</Typography>
                )}
              </Box>
            )}

            {/* Only show Room section if we have a room ID or name */}
            {(record.roomId || record.roomName) && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  {tRoom}
                </Typography>
                {record.roomId ? (
                  <Link to={Route.roomView({ roomId: record.roomId })} type="internal">
                    {record.roomName}
                  </Link>
                ) : (
                  <Typography>{record.roomName}</Typography>
                )}
              </Box>
            )}

            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                {tOrganization}
              </Typography>
              <Typography>{record.bubbleName}</Typography>
            </Box>

            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                {tVolunteer}
              </Typography>
              <Typography>{record.volunteerDisplayName}</Typography>
            </Box>

            {record.leaderDisplayName && (
              <Box>
                <Typography color="text.secondary" variant="subtitle2">
                  {tLeader}
                </Typography>
                <Typography>{record.leaderDisplayName}</Typography>
              </Box>
            )}

            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                {tTime}
              </Typography>
              <Typography>{startTime && formatEventTime(startTime)}</Typography>
            </Box>

            {showConfirmButton && (
              <Button disabled={isConfirming} fullWidth onClick={handleConfirm} variant="contained">
                {tConfirm}
              </Button>
            )}
            <Button
              fullWidth
              onClick={handleDelete}
              variant="outlined"
              color="error"
              sx={{ mt: showConfirmButton ? 2 : 0 }}
            >
              {tDelete}
            </Button>
          </Stack>
        </Box>
      </Drawer>
      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        itemType="volunteering record"
        itemName={`${record.volunteerDisplayName} - ${record.eventName ?? record.roomName ?? ""}`}
        itemDescription={startTime ? formatEventTime(startTime) : undefined}
      />
    </>
  );
};
